import React, { useState, useEffect } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { SendWhatsAppMessage } from '../../../../api/WhatsappAPI';
import MessagesTemplate from '../../../../consts/MessagesTemplate';
import CalendarConfig from '../../../../consts/CalendarConfig';
import { DOMAIN_URL } from '../../../../consts/DomainConfig';
import MessageTemplateSelector from '../../../../components/message-template-selector/MessageTemplateSelector';
import { UpdateAppointmentStatusAPI, PrepareManualConfirmationMessageAPI, SendManualConfirmationRequestAPI } from '../../../../api/API';
import { useCanSendAgendaWhatsApp } from '../../../appointments/v2/hooks/useAgendaWhatsAppSend';
import Toast from '../../../../components/support/Toast';
import AppointmentsState from '../../../../consts/AppointmentsState';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import PatientItem from '../shared/PatientItem';
import { ContactActions } from '../shared/IconAction';
import { formatDayLabel, formatTime } from '../../utils/dashboardDates';
import { procedureLabels } from '../../../appointments/v2/utils/appointmentUtils';
import styles from './ActionCenter.module.css';

const selectSx = {
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: '10px',
  height: '32px',
  minWidth: '112px',
  background: '#FFFFFF',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6EAF5' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D5DEFF' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#9CB2FD', borderWidth: '1px' },
  '& .MuiSelect-select': { padding: '4px 10px', paddingRight: '28px !important' },
};

// Plano ou número não permitem esse modelo pelo sistema: segue no WhatsApp Web
const WEB_FALLBACK_REASONS = ['PLAN_REQUIRED', 'OWN_NUMBER_ONLY'];

const menuProps = {
  PaperProps: {
    sx: {
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(90,128,251,0.14)',
      border: '1px solid #E9EDFA',
      mt: 0.5,
    },
  },
  MenuListProps: { style: { padding: '4px' } },
};

/** Agendamento sem confirmação: horário, paciente, status e lembrete pelo WhatsApp. */
function AppointmentRow({ appointment }) {
  const { CompanyConfig, GetPatientById, GetResourceById, GetProcedureById } = DataProviderInstance();
  const templates = MessagesTemplate.getTemplates(CompanyConfig);
  const canSendViaSystem = useCanSendAgendaWhatsApp();

  const messageStatusKey = `messageStatus-v2-${appointment.id}`;

  const [isTemplateSelectorOpen, setIsTemplateSelectorOpen] = useState(false);
  const [sentMessages, setSentMessages] = useState(() => {
    const stored = localStorage.getItem(messageStatusKey);
    return stored ? JSON.parse(stored) : [];
  });
  const [currentStatus, setCurrentStatus] = useState(appointment.status);
  const [feedback, setFeedback] = useState(null); // { message, severity }

  useEffect(() => {
    localStorage.setItem(messageStatusKey, JSON.stringify(sentMessages));
  }, [sentMessages, messageStatusKey]);

  // appointment.patient, .resource e .procedure são IDs numéricos — sempre resolver via DataProvider
  const patient = appointment.patient ? GetPatientById(appointment.patient)
    : (appointment.patient_id ? GetPatientById(appointment.patient_id) : null);
  const resource = appointment.resource ? GetResourceById(appointment.resource)
    : (appointment.resource_id ? GetResourceById(appointment.resource_id) : null);
  const procedure = appointment.procedure ? GetProcedureById(appointment.procedure)
    : (appointment.procedure_id ? GetProcedureById(appointment.procedure_id) : null);
  // Vários procedimentos: "A + B + C"; sem linha gravada cai no principal
  const procedureNames = procedureLabels(appointment, CompanyConfig?.Procedures);

  const patientName = patient?.name || appointment.patient_name || 'Paciente';
  const patientPhone = patient?.phone || appointment.patient_phone || '';
  // DDI do paciente — necessário para o WhatsApp resolver o número corretamente
  const patientCountry = patient?.countryCode || '';
  const patientId = patient?.id || appointment.patient_id || appointment.id;
  const resourceName = resource?.Label || appointment.resource_name || '';
  const procedureName = procedureNames || procedure?.Label || appointment.procedure_name || '';
  const appointmentDate = appointment.date;
  const timeLabel = formatTime(appointmentDate);

  // Usado nas mensagens de template ("Hoje às 14:00")
  const appointmentTime = `${formatDayLabel(appointmentDate)} às ${timeLabel}`;

  function markMessageAsSent(index) {
    setSentMessages(prev => (prev.includes(index) ? prev : [...prev, index]));
  }

  /**
   * Com envio pelo sistema (capabilities.agenda_send), o pedido sai na hora com
   * os botões do cron do modelo escolhido. Sem ele, ou se o plano/número não
   * permitem esse modelo, abre o WhatsApp Web como antes.
   */
  function handleSendMessage(templateIndex) {
    setIsTemplateSelectorOpen(false);

    if (!canSendViaSystem) {
      sendViaWhatsAppWeb(templateIndex);
      return;
    }

    SendManualConfirmationRequestAPI(appointment.id, templateIndex, (result) => {
      if (result?.success) {
        markMessageAsSent(templateIndex);
        setFeedback({
          severity: 'success',
          message: result.status === 'sent'
            ? 'Pedido de confirmação enviado pelo WhatsApp.'
            : 'Pedido de confirmação entrou na fila de envio do WhatsApp.',
        });
        return;
      }
      if (WEB_FALLBACK_REASONS.includes(result?.reason)) {
        sendViaWhatsAppWeb(templateIndex);
        return;
      }
      setFeedback({ severity: 'error', message: result?.error || 'Não foi possível enviar a mensagem pelo WhatsApp.' });
    });
  }

  /**
   * Monta a mensagem de confirmação pelo backend (fluxo V2, com link público
   * de confirmação) e abre o WhatsApp Web. Se a chamada falhar, monta
   * localmente (fluxo antigo).
   */
  function sendViaWhatsAppWeb(templateIndex) {
    PrepareManualConfirmationMessageAPI(appointment.id, templateIndex, (result) => {
      if (result && result.success && result.message) {
        SendWhatsAppMessage(patientPhone, MessagesTemplate.ConvertToWhatsAppText(result.message), patientCountry);
        markMessageAsSent(templateIndex);
      } else {
        const message = buildConfirmationMessageLegacy(templateIndex);
        if (message && patientPhone) {
          SendWhatsAppMessage(patientPhone, MessagesTemplate.ConvertToWhatsAppText(message), patientCountry);
          markMessageAsSent(templateIndex);
        }
      }
    });
  }

  /** Montagem local antiga — só quando a API falha (usa o confirm_appointment.php, sem token público). */
  function buildConfirmationMessageLegacy(templateIndex) {
    let template = templates.AppointmentAskForConfirmation[templateIndex];
    if (!template) return '';

    const yyyymmdd = CalendarConfig.GetAAAAMMDDArray(appointmentDate);
    const monthDay = yyyymmdd[2];
    const monthName = CalendarConfig.Months[yyyymmdd[1] - 1];
    const urlToConfirm = DOMAIN_URL + 'api/confirm_appointment.php/?id=' + appointment.id;

    const today = new Date();
    const apptDate = new Date(appointmentDate);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const currentHour = new Date().getHours();
    let greetings = 'Bom dia';
    if (currentHour >= 12 && currentHour < 18) greetings = 'Boa tarde';
    else if (currentHour >= 18) greetings = 'Boa noite';

    let when = '';
    if (apptDate.toDateString() === today.toDateString()) when = 'hoje';
    else if (apptDate.toDateString() === tomorrow.toDateString()) when = 'amanhã';

    let message = template;
    message = message.replaceAll('{company_name}', CompanyConfig?.Name || '');
    message = message.replaceAll('{greetings}', greetings);
    message = message.replaceAll('{name}', patientName.split(' ')[0]);
    message = message.replaceAll('{procedure}', procedureName);
    message = message.replaceAll('{doctor_name}', resourceName);
    message = message.replaceAll('{when}', when);
    message = message.replaceAll('{day}', monthDay);
    message = message.replaceAll('{month}', monthName);
    message = message.replaceAll('{time}', appointmentTime);
    message = message.replaceAll('{url}', urlToConfirm);
    message = message.replaceAll('{confirmation_link}', urlToConfirm);

    return message;
  }

  function handleStatusChange(event) {
    const newStatus = event.target.value;
    setCurrentStatus(newStatus);
    UpdateAppointmentStatusAPI(appointment.id, newStatus, () => {});
  }

  const statusColor = AppointmentsState.GetDataFromId(currentStatus)?.textColor || '#4A5568';

  return (
    <>
      <PatientItem
        name={patientName}
        patientId={patientId}
        sent={sentMessages.length > 0}
        sentTitle={canSendViaSystem ? 'Mensagem enviada' : 'Mensagem aberta no WhatsApp Web'}
        leading={<span className={styles.time}>{timeLabel}</span>}
        meta={[procedureName, resourceName].filter(Boolean).join(' · ')}
        actions={(
          <>
            <Select
              value={currentStatus || ''}
              onChange={handleStatusChange}
              variant="outlined"
              size="small"
              sx={{ ...selectSx, color: statusColor }}
              MenuProps={menuProps}
              className={styles.statusSelect}
            >
              {AppointmentsState.ChangeableBySystem.map((option) => {
                const data = AppointmentsState.GetDataFromId(option);
                return (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{ fontSize: '0.8rem', fontWeight: 500, color: data?.textColor || 'inherit', borderRadius: '8px' }}
                  >
                    {data?.label || option}
                  </MenuItem>
                );
              })}
            </Select>
            {patientPhone && (
              <ContactActions
                whatsappTitle="Enviar lembrete de confirmação"
                onWhatsApp={() => setIsTemplateSelectorOpen(true)}
                phone={patientPhone}
              />
            )}
          </>
        )}
      />
      <MessageTemplateSelector
        open={isTemplateSelectorOpen}
        title="Escolha a mensagem a ser enviada"
        templates={templates.AppointmentAskForConfirmation}
        onSelect={(index) => handleSendMessage(index)}
        onClose={() => setIsTemplateSelectorOpen(false)}
        appointmentData={{ id: appointment.id, date: appointmentDate }}
        patientData={{ name: patientName, phone: patientPhone }}
        resourceData={resource || { Label: resourceName }}
        procedureData={{ ...(procedure || {}), Label: procedureName }}
        companyName={CompanyConfig?.Name || ''}
        viaWhatsAppWeb={!canSendViaSystem}
      />
      <Toast
        open={!!feedback}
        message={feedback?.message || ''}
        severity={feedback?.severity || 'success'}
        onClose={() => setFeedback(null)}
      />
    </>
  );
}

export default AppointmentRow;
